/**
 * Call Reports data pipeline (CRM ▸ Reports ▸ Call Reports).
 *
 * Merges four sources into one list of Implementation Hours calls:
 *   - Calendly        — every department Strategy Call / 1 on 1 booking, org-wide
 *                       (callable fetchCalendlyImplementationCalls)
 *   - Google Calendar — each connected teammate's calendar; adds Meet/Calendar links to
 *                       Calendly bookings and catches calls booked outside Calendly
 *   - CRM hours logs  — the Implementation Hours deliverable logs that actually deduct hours
 *   - Fathom          — meeting_summaries recordings + summaries
 * and reconciles them against the hours log: a held call with no log, or a canceled call
 * whose log is still there (logs count toward used hours regardless of meetingStatus), is
 * flagged so client balances stay accurate.
 */

import { collection, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { fetchGoogleCalendarEvents } from './googleCalendarService';
import { fetchPortalUsersLinkedToAccounts } from './accountTeamMembersService';
import { matchesDepartmentTitle } from './departmentCallsService';
import { isStrategyCallDeliverableName } from '../utils/programPricing';
import { TASK_TIME_DEPARTMENTS } from '../utils/taskTimeConstants';
import { isActiveClientAccount as isActiveClientLifecycle } from '../utils/accountLifecycle';
import {
  buildClientEventRoster,
  calendarEventDedupeKey,
  calendarNamesLikelyMatch,
  calendarParticipantDisplayNormalized,
  canonicalCalendarEmailKey,
  eventMatchesClientRoster,
  resolveCalendarEventHost,
} from '../utils/clientCalendarEventMatching';

// Implementation Hours call names. Keep in sync with isImplementationCallName in
// functions/fetchCalendlyImplementationCalls.js.
export const IMPLEMENTATION_CALL_PATTERN =
  /strategy\s*call|1:1\s*strat|strat\s*call|implementation\s*hours?|\b1\s*(?:on|:)\s*1\b/i;

/**
 * Calendly bookings land on the host's Google Calendar titled "<Invitee> and <Host>", with
 * the event type on the line after "Event Name", a calendly.com/events/<uuid> link, and the
 * invitee's prep answer. Returns null for anything Calendly didn't book.
 */
function parseCalendlyBooking(description) {
  const text = String(description || '').replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '');
  const nameMatch = text.match(/event name:?[ \t]*\n?[ \t]*([^\n]+)/i);
  const uuidMatch = text.match(/calendly\.com\/events\/([0-9a-f-]{36})/i);
  if (!nameMatch && !uuidMatch) return null;
  const prepMatch = text.match(/help prepare for our meeting\.?:?[ \t]*([^\n]+)/i);
  return {
    eventName: nameMatch ? nameMatch[1].trim() : null,
    calendlyUri: uuidMatch ? `https://api.calendly.com/scheduled_events/${uuidMatch[1].toLowerCase()}` : null,
    prepNote: prepMatch ? prepMatch[1].trim() : null,
  };
}

const calendlyUuid = (uri) => String(uri || '').split('/').pop().toLowerCase();

// Scheduling-tool blocks that borrow the call's name, e.g.
// "[30-minute buffer before Technology Strategy Call event]". Never real calls.
const CALENDAR_PLACEHOLDER_PATTERN = /\bbuffer\b|\btravel time\b|^\s*\[[^\]]*\]\s*$/i;

const GENERIC_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'ymail.com', 'hotmail.com', 'outlook.com', 'live.com',
  'msn.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com', 'comcast.net', 'att.net', 'sbcglobal.net',
  'verizon.net', 'bellsouth.net', 'cox.net', 'charter.net', 'earthlink.net', 'protonmail.com',
  'proton.me', 'mail.com', 'gmx.com', 'zoho.com',
]);

const FATHOM_MATCH_WINDOW_MS = 90 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

export const CALL_STATUS = {
  COMPLETED: 'completed',
  UPCOMING: 'upcoming',
  CANCELED: 'canceled',
  RESCHEDULED: 'rescheduled',
  NO_SHOW: 'no_show',
};

export const LOG_STATUS = {
  LOGGED: 'logged',
  NOT_LOGGED: 'not_logged',
  LOGGED_CANCELED: 'logged_canceled',
  UNMATCHED: 'unmatched',
  NA: 'n/a',
};

// Every held call should have a Fathom recording in meeting_summaries.
export const RECORDING_STATUS = {
  RECORDED: 'recorded',
  NOT_RECORDED: 'not_recorded',
  PENDING: 'pending',
  NA: 'n/a',
};

// Fathom posts the recording once it finishes processing, so a call that just ended
// isn't "missing" yet.
const RECORDING_GRACE_MS = 3 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === 'function') return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const pad2 = (n) => String(n).padStart(2, '0');
const ymdLocal = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const ymdUtc = (d) => d.toISOString().slice(0, 10);

function dayNumber(ymd) {
  const [y, m, d] = String(ymd).split('-').map(Number);
  if (!y || !m || !d) return null;
  return Date.UTC(y, m - 1, d) / DAY_MS;
}

function emailDomain(email) {
  const s = String(email || '').trim().toLowerCase();
  const at = s.lastIndexOf('@');
  return at > 0 ? s.slice(at + 1) : '';
}

function websiteDomain(url) {
  const s = String(url || '').trim();
  if (!s) return '';
  try {
    const u = new URL(/^https?:\/\//i.test(s) ? s : `https://${s}`);
    return u.hostname.replace(/^www\./, '').toLowerCase();
  } catch (e) {
    return '';
  }
}

function minutesBetween(start, end) {
  if (!start || !end) return null;
  const mins = Math.round((end.getTime() - start.getTime()) / 60000);
  return mins > 0 ? mins : null;
}

function isAllDayEvent(start, end) {
  if (!start || !end) return false;
  const span = end.getTime() - start.getTime();
  return span > 0 && span % DAY_MS === 0 && start.getUTCHours() === 0 && start.getUTCMinutes() === 0;
}

export function internalUserName(u) {
  if (!u) return '';
  return u.name || [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email || u.id;
}

export function classifyCallDepartment(...texts) {
  for (const text of texts) {
    if (!text) continue;
    for (const dept of TASK_TIME_DEPARTMENTS) {
      if (matchesDepartmentTitle(text, dept.value)) return dept.value;
    }
  }
  return null;
}

/** True for client accounts that are still being served (same rule as Meeting Mode). */
export function isActiveClientAccount(account) {
  return isActiveClientLifecycle(account);
}

// ---------------------------------------------------------------------------
// Account + internal user matching
// ---------------------------------------------------------------------------

function buildInternalDirectory(internalUsers) {
  const byEmailKey = new Map();
  const domains = new Set(['buildersbreakthrough.com']);
  (internalUsers || []).forEach((u) => {
    const key = canonicalCalendarEmailKey(u.email);
    if (key) byEmailKey.set(key, u);
    const d = emailDomain(u.email);
    if (d && !GENERIC_EMAIL_DOMAINS.has(d)) domains.add(d);
  });
  const isInternalEmail = (email) => {
    const key = canonicalCalendarEmailKey(email);
    if (!key) return false;
    return byEmailKey.has(key) || domains.has(emailDomain(key));
  };
  const isResourceEmail = (email) => /(calendar\.google\.com|calendly\.com)$/i.test(emailDomain(email));
  const findByEmail = (email) => byEmailKey.get(canonicalCalendarEmailKey(email)) || null;
  return { isInternalEmail, isResourceEmail, findByEmail, domains };
}

// internal_users store firstName/lastName (no `name`), so every host goes through here.
function hostFromInternalUser(u) {
  if (!u) return null;
  return {
    id: u.id,
    name: internalUserName(u),
    email: u.email || '',
    profileImage: u.profileImage || u.photoURL || u.avatarUrl || null,
    // Each teammate connects Fathom in My Profile; without it their recordings never reach the CRM.
    fathomConnected: !!u.integrations?.fathom?.connected,
  };
}

function buildAccountMatcher(clients, membersByAccountId, directory) {
  const byEmail = new Map();
  const domainOwners = new Map();
  const rosters = [];

  const addDomain = (domain, accountId) => {
    if (!domain || GENERIC_EMAIL_DOMAINS.has(domain) || directory.domains.has(domain)) return;
    if (!domainOwners.has(domain)) domainOwners.set(domain, new Set());
    domainOwners.get(domain).add(accountId);
  };

  clients.forEach((account) => {
    const members = (membersByAccountId.get(account.id) || []).map((m) => ({ name: m.name, email: m.email }));
    const roster = buildClientEventRoster(account, members);
    rosters.push({ account, roster });
    roster.rosterKeys.forEach((key) => {
      if (!byEmail.has(key)) byEmail.set(key, account.id);
      addDomain(emailDomain(key), account.id);
    });
    addDomain(websiteDomain(account.website), account.id);
  });

  // A company domain only identifies an account when exactly one client uses it.
  const byDomain = new Map();
  domainOwners.forEach((ids, domain) => {
    if (ids.size === 1) byDomain.set(domain, [...ids][0]);
  });

  const isExternal = (email) => email && !directory.isInternalEmail(email) && !directory.isResourceEmail(email);

  const matchEmails = (emails) => {
    const external = (emails || []).filter(isExternal);
    for (const email of external) {
      const id = byEmail.get(canonicalCalendarEmailKey(email));
      if (id) return { accountId: id, matchedBy: 'email' };
    }
    for (const email of external) {
      const id = byDomain.get(emailDomain(email));
      if (id) return { accountId: id, matchedBy: 'domain' };
    }
    return null;
  };

  const fullName = (name) => calendarParticipantDisplayNormalized(name).split(' ').filter(Boolean).length >= 2;

  const matchName = (name) => {
    if (!fullName(name)) return null;
    for (const { account, roster } of rosters) {
      if (roster.primaryContactDn && fullName(roster.primaryContactDn) && calendarNamesLikelyMatch(roster.primaryContactDn, name)) {
        return { accountId: account.id, matchedBy: 'name' };
      }
      for (const m of roster.members) {
        if (fullName(m.name) && calendarNamesLikelyMatch(m.name, name)) return { accountId: account.id, matchedBy: 'name' };
      }
    }
    return null;
  };

  // The full Events-tab roster scan (names, description text) only runs for events that
  // already look like implementation calls — it's too loose for every calendar event.
  const matchGoogleEvent = (event, allowRosterScan) => {
    const emails = [
      ...(event.attendees || []).map((a) => a.email),
      event.organizer?.email,
      event.creator?.email,
    ];
    const hit = matchEmails(emails);
    if (hit || !allowRosterScan) return hit;
    for (const { account, roster } of rosters) {
      if (eventMatchesClientRoster(event, roster)) return { accountId: account.id, matchedBy: 'calendar' };
    }
    return null;
  };

  return { matchEmails, matchName, matchGoogleEvent, isExternal };
}

// ---------------------------------------------------------------------------
// Source → call records
// ---------------------------------------------------------------------------

function baseCall(overrides) {
  return {
    id: '',
    title: '',
    department: null,
    start: null,
    end: null,
    durationMinutes: null,
    timeKnown: true,
    status: CALL_STATUS.COMPLETED,
    kind: 'implementation',
    sources: [],
    accountId: null,
    accountName: null,
    matchedBy: null,
    invitee: null,
    invitees: [],
    host: null,
    bookedAt: null,
    cancellation: null,
    notes: [],
    joinUrl: null,
    googleHtmlLink: null,
    googleEventId: null,
    calendlyUri: null,
    recording: null,
    recordingStatus: RECORDING_STATUS.NA,
    log: null,
    logStatus: LOG_STATUS.NA,
    ...overrides,
  };
}

function callFromCalendly(ev, ctx) {
  const start = toDate(ev.startTime);
  const end = toDate(ev.endTime);
  const invitees = ev.invitees || [];
  const primary = invitees.find((i) => i.status === 'active') || invitees[0] || null;
  const isCanceled = ev.status === 'canceled';
  const rescheduled = isCanceled && invitees.some((i) => i.rescheduled);
  const noShow = !isCanceled && invitees.some((i) => i.noShow);

  let status;
  if (isCanceled) status = rescheduled ? CALL_STATUS.RESCHEDULED : CALL_STATUS.CANCELED;
  else if (noShow) status = CALL_STATUS.NO_SHOW;
  else if (start && start > ctx.now) status = CALL_STATUS.UPCOMING;
  else status = CALL_STATUS.COMPLETED;

  // Guests the invitee added (often the rest of the client's team) help match the account too.
  const match =
    ctx.matcher.matchEmails([...invitees.map((i) => i.email), ...(ev.guests || [])]) ||
    (primary?.name ? ctx.matcher.matchName(primary.name) : null);

  const hostRef = (ev.hosts || [])[0] || null;
  const host =
    hostFromInternalUser(hostRef?.email ? ctx.directory.findByEmail(hostRef.email) : null) ||
    (hostRef ? { id: null, name: hostRef.name || hostRef.email || 'Unknown', email: hostRef.email || '', profileImage: null } : null);

  const cancellation = isCanceled
    ? {
        by: ev.cancellation?.canceledBy || null,
        byType: ev.cancellation?.cancelerType || null,
        reason: ev.cancellation?.reason || primary?.cancelReason || null,
        at: toDate(ev.cancellation?.createdAt),
      }
    : null;

  return baseCall({
    id: `calendly:${ev.uri}`,
    title: ev.name || 'Strategy Call',
    department: classifyCallDepartment(ev.name),
    start,
    end,
    durationMinutes: ev.durationMinutes ?? minutesBetween(start, end),
    status,
    sources: ['calendly'],
    accountId: match?.accountId || null,
    matchedBy: match?.matchedBy || null,
    invitee: primary ? { name: primary.name || '', email: primary.email || '' } : null,
    invitees: [
      ...invitees.map((i) => ({ name: i.name || '', email: i.email || '', status: i.status })),
      ...(ev.guests || []).map((email) => ({ name: '', email, status: 'guest' })),
    ],
    host,
    bookedAt: toDate(ev.createdAt),
    cancellation,
    notes: primary?.notes || [],
    joinUrl: ev.joinUrl || null,
    googleEventId: ev.googleEventId || null,
    calendlyUri: ev.uri,
  });
}

function dedupeGoogleEvents(googleResults) {
  const map = new Map();
  (googleResults || []).forEach(({ user, events }) => {
    (events || []).forEach((event) => {
      const key = calendarEventDedupeKey(event);
      const existing = map.get(key);
      if (!existing) {
        map.set(key, { event, fetchedBy: user, key });
      } else if ((event.attendees || []).length > (existing.event.attendees || []).length) {
        map.set(key, { event, fetchedBy: existing.fetchedBy, key });
      }
    });
  });
  return [...map.values()];
}

const minuteKey = (d) => Math.round(d.getTime() / 60000);

/**
 * Attach each Calendly booking's Google Calendar copy. Exact joins first — Calendly's
 * calendar_event.external_id is the Google event id, and the Google description carries the
 * Calendly event uuid — then same start minute + a shared invitee for anything older.
 */
function mergeGoogleIntoCalendly(calls, googleEntries) {
  const byGoogleId = new Map();
  const byCalendlyUuid = new Map();
  const byMinute = new Map();
  googleEntries.forEach((entry) => {
    if (entry.event.googleEventId) byGoogleId.set(entry.event.googleEventId, entry);
    const booking = parseCalendlyBooking(entry.event.description);
    if (booking?.calendlyUri) byCalendlyUuid.set(calendlyUuid(booking.calendlyUri), entry);
    const start = toDate(entry.event.start);
    if (!start) return;
    const k = minuteKey(start);
    if (!byMinute.has(k)) byMinute.set(k, []);
    byMinute.get(k).push(entry);
  });

  const consumed = new Set();
  const attach = (call, entry) => {
    consumed.add(entry.key);
    call.sources.push('google');
    call.googleHtmlLink = entry.event.htmlLink || null;
    call.googleEventId = entry.event.googleEventId || call.googleEventId;
    call.joinUrl = call.joinUrl || entry.event.hangoutLink || null;
  };

  calls.forEach((call) => {
    const exact =
      (call.googleEventId && byGoogleId.get(call.googleEventId)) ||
      (call.calendlyUri && byCalendlyUuid.get(calendlyUuid(call.calendlyUri)));
    if (exact && !consumed.has(exact.key)) attach(call, exact);
  });

  calls.forEach((call) => {
    if (call.sources.includes('google') || !call.start) return;
    if (call.status === CALL_STATUS.CANCELED || call.status === CALL_STATUS.RESCHEDULED) return;
    const candidates = (byMinute.get(minuteKey(call.start)) || []).filter((e) => !consumed.has(e.key));
    if (candidates.length === 0) return;
    const inviteeKeys = new Set(call.invitees.map((i) => canonicalCalendarEmailKey(i.email)).filter(Boolean));
    const pick = candidates.find((e) => (e.event.attendees || []).some((a) => inviteeKeys.has(canonicalCalendarEmailKey(a.email))));
    if (pick) attach(call, pick);
  });
  return consumed;
}

function callFromGoogle(entry, ctx) {
  const { event, fetchedBy } = entry;
  const start = toDate(event.start);
  const end = toDate(event.end);
  if (!start || isAllDayEvent(start, end)) return null;

  const rawTitle = event.title || event.summary || '';
  if (CALENDAR_PLACEHOLDER_PATTERN.test(rawTitle)) return null;
  // Calendly titles the invite "<Invitee> and <Host>"; its event type name is the real title.
  const booking = parseCalendlyBooking(event.description);
  const title = booking?.eventName || rawTitle;
  const isImplementation = IMPLEMENTATION_CALL_PATTERN.test(title) || IMPLEMENTATION_CALL_PATTERN.test(rawTitle);
  const external = (event.attendees || []).filter((a) => ctx.matcher.isExternal(a.email));
  // A call needs someone outside BBA on the invite; attendee-less blocks are holds,
  // unless Calendly's booking details say it's a real booking.
  if (external.length === 0 && !booking) return null;

  const match = ctx.matcher.matchGoogleEvent(event, isImplementation);
  // Non-implementation events only count as "client meetings" when a client is on them.
  if (!isImplementation && !match) return null;

  const declined = external.length > 0 && external.every((a) => a.responseStatus === 'declined');
  const status = declined ? CALL_STATUS.CANCELED : start > ctx.now ? CALL_STATUS.UPCOMING : CALL_STATUS.COMPLETED;
  // Organizer/creator → internal user, else whoever's calendar it came from (Events-tab rule).
  const resolved = resolveCalendarEventHost(event, fetchedBy, ctx.internalUsers);
  const host =
    hostFromInternalUser(resolved?.id ? ctx.internalById.get(resolved.id) : null) ||
    (resolved?.name ? { ...resolved, id: null } : null);

  // For Calendly invites the booker's name is the first half of "<Invitee> and <Host>";
  // pick the attendee whose address carries their first name.
  const bookerName = booking ? (rawTitle.split(/\s+and\s+/i)[0] || '').trim() : '';
  const bookerFirst = bookerName.split(/\s+/)[0]?.toLowerCase() || '';
  const primary =
    (bookerFirst && external.find((a) => String(a.email || '').toLowerCase().split('@')[0].includes(bookerFirst))) ||
    external[0] ||
    null;

  return baseCall({
    id: `google:${entry.key}`,
    title: title || 'Calendar event',
    department: classifyCallDepartment(title),
    start,
    end,
    durationMinutes: minutesBetween(start, end),
    status,
    kind: isImplementation ? 'implementation' : 'meeting',
    sources: ['google'],
    accountId: match?.accountId || null,
    matchedBy: match?.matchedBy || null,
    invitee: primary || bookerName
      ? { name: primary?.displayName || bookerName || '', email: primary?.email || '' }
      : null,
    invitees: external.map((a) => ({ name: a.displayName || '', email: a.email || '', status: a.responseStatus })),
    host,
    cancellation: declined ? { reason: 'Invitee declined the calendar invite', by: null, byType: 'invitee', at: null } : null,
    notes: booking?.prepNote ? [{ question: 'Prep notes', answer: booking.prepNote }] : [],
    joinUrl: event.hangoutLink || null,
    googleHtmlLink: event.htmlLink || null,
    googleEventId: event.googleEventId || null,
    calendlyUri: booking?.calendlyUri || null,
  });
}

function recordingFromSummary(s) {
  const text = String(s.summary || '').trim();
  return {
    id: s.id,
    url: s.recordingUrl || s.shareUrl || null,
    title: s.meetingTitle || '',
    summary: text.length > 700 ? `${text.slice(0, 700).trim()}…` : text,
    actionItemCount: Array.isArray(s.actionItems) ? s.actionItems.length : 0,
  };
}

function pinRecording(call, s) {
  call.recording = recordingFromSummary(s);
  if (!call.sources.includes('fathom')) call.sources.push('fathom');
}

/** Pin Fathom recordings onto held calls; returns summaries that matched nothing. */
function attachRecordings(calls, summaries) {
  const consumed = new Set();
  const held = calls.filter((c) => c.start && (c.status === CALL_STATUS.COMPLETED || c.status === CALL_STATUS.NO_SHOW));
  const withStart = summaries.map((s) => ({ s, start: toDate(s.scheduledStartTime) }));

  const take = (call, entry) => {
    consumed.add(entry.s.id);
    pinRecording(call, entry.s);
  };
  const near = (call, entry) => entry.start && Math.abs(entry.start - call.start) <= FATHOM_MATCH_WINDOW_MS;

  held.forEach((call) => {
    if (!call.googleEventId) return;
    const hit = withStart.find((e) => !consumed.has(e.s.id) && e.s.googleEventId && e.s.googleEventId === call.googleEventId);
    if (hit) take(call, hit);
  });
  held.forEach((call) => {
    if (call.recording || !call.accountId) return;
    const hit = withStart.find((e) => !consumed.has(e.s.id) && e.s.accountId === call.accountId && near(call, e));
    if (hit) take(call, hit);
  });
  held.forEach((call) => {
    if (call.recording) return;
    const keys = new Set(call.invitees.map((i) => canonicalCalendarEmailKey(i.email)).filter(Boolean));
    if (keys.size === 0) return;
    const hit = withStart.find((e) => !consumed.has(e.s.id) && near(call, e) &&
      (e.s.inviteeEmails || []).some((em) => keys.has(canonicalCalendarEmailKey(em))));
    if (hit) take(call, hit);
  });

  return withStart.filter((e) => !consumed.has(e.s.id));
}

/**
 * Hours-log-only calls usually have no start time (just the log date), so leftover
 * recordings match them by account + day instead of the ±90 min window.
 */
function attachRecordingsByDay(logCalls, leftovers) {
  const consumed = new Set();
  logCalls.forEach((call) => {
    if (call.recording || !call.accountId || call.status !== CALL_STATUS.COMPLETED || !call.log?.dateKey) return;
    const hit = leftovers.find((e) => !consumed.has(e.s.id) && e.start && e.s.accountId === call.accountId &&
      (ymdLocal(e.start) === call.log.dateKey || ymdUtc(e.start) === call.log.dateKey));
    if (!hit) return;
    consumed.add(hit.s.id);
    pinRecording(call, hit.s);
  });
}

function callFromRecording({ s, start }, ctx) {
  if (!start || !s.accountId) return null;
  if (!IMPLEMENTATION_CALL_PATTERN.test(s.meetingTitle || '')) return null;
  return baseCall({
    id: `fathom:${s.id}`,
    title: s.meetingTitle || 'Recorded call',
    department: s.department || classifyCallDepartment(s.meetingTitle),
    start,
    status: CALL_STATUS.COMPLETED,
    sources: ['fathom'],
    accountId: s.accountId,
    matchedBy: 'fathom',
    host: hostFromInternalUser(s.recordedByEmail ? ctx.directory.findByEmail(s.recordedByEmail) : null),
    invitees: (s.inviteeEmails || []).filter((e) => ctx.matcher.isExternal(e)).map((email) => ({ name: '', email, status: null })),
    googleEventId: s.googleEventId || null,
    recording: recordingFromSummary(s),
  });
}

// ---------------------------------------------------------------------------
// CRM hours logs
// ---------------------------------------------------------------------------

/**
 * Every call log on each client's Implementation Hours deliverable (approved task time excluded).
 * Webhook-written deliverable entries carry no `name`, so `catalogNames` (item id → catalog name)
 * identifies the Implementation Hours row the same way the CRM Program tab does.
 */
export function collectHoursLogs(clients, catalogNames = new Map()) {
  const logs = [];
  (clients || []).forEach((account) => {
    Object.entries(account.deliverables || {}).forEach(([deliverableKey, deliv]) => {
      const name = String(deliv?.name || catalogNames.get(deliverableKey) || '');
      const isCallDeliverable = isStrategyCallDeliverableName(name.toLowerCase()) || name.includes('1:1');
      (deliv?.logs || []).forEach((log, index) => {
        if (!log || log.source === 'task-time') return;
        if (!isCallDeliverable && !log.calendlyEventUri && !log.scheduledStart) return;
        const dateKey = log.date ? String(log.date).slice(0, 10) : null;
        const when = toDate(log.scheduledStart) || (dateKey ? toDate(`${dateKey}T12:00:00`) : null);
        logs.push({
          key: `${account.id}:${deliverableKey}:${log.id || index}`,
          accountId: account.id,
          deliverableName: name || 'Implementation Hours',
          id: log.id || null,
          dateKey: dateKey || (when ? ymdLocal(when) : null),
          when,
          timeKnown: !!log.scheduledStart,
          quantity: Number(log.quantity) > 0 ? Number(log.quantity) : 1,
          completedWith: log.completedWith || null,
          meetingStatus: log.meetingStatus || null,
          calendlyEventUri: log.calendlyEventUri || null,
          durationMinutes: Number(log.durationMinutes) > 0 ? Number(log.durationMinutes) : null,
          title: log.eventName || log.title || null,
        });
      });
    });
  });
  return logs;
}

function reconcileLogs(calls, logs) {
  const used = new Set();
  const attach = (call, log) => {
    used.add(log.key);
    call.log = log;
    if (!call.sources.includes('crm')) call.sources.push('crm');
  };

  const byUri = new Map(logs.filter((l) => l.calendlyEventUri).map((l) => [l.calendlyEventUri, l]));
  calls.forEach((call) => {
    if (!call.calendlyUri) return;
    const log = byUri.get(call.calendlyUri);
    if (log && !used.has(log.key)) attach(call, log);
  });

  const byAccount = new Map();
  logs.forEach((l) => {
    if (!byAccount.has(l.accountId)) byAccount.set(l.accountId, []);
    byAccount.get(l.accountId).push(l);
  });

  // Webhook logs store the booking's UTC date, so a call matches a log within a day.
  const dateMatch = (call) => {
    if (call.log || !call.accountId || !call.start || call.kind !== 'implementation') return;
    const callDays = [ymdLocal(call.start), ymdUtc(call.start)].map(dayNumber);
    let best = null;
    let bestScore = Infinity;
    (byAccount.get(call.accountId) || []).forEach((log) => {
      if (used.has(log.key) || !log.dateKey) return;
      if (call.calendlyUri && log.calendlyEventUri && log.calendlyEventUri !== call.calendlyUri) return;
      const logDay = dayNumber(log.dateKey);
      if (logDay == null) return;
      const diff = Math.min(...callDays.map((d) => Math.abs(d - logDay)));
      if (diff > 1) return;
      const score = diff + (call.host?.id && log.completedWith === call.host.id ? 0 : 0.5);
      if (score < bestScore) {
        best = log;
        bestScore = score;
      }
    });
    if (best) attach(call, best);
  };

  const isActive = (c) => c.status !== CALL_STATUS.CANCELED && c.status !== CALL_STATUS.RESCHEDULED;
  calls.filter(isActive).forEach(dateMatch);
  calls.filter((c) => !isActive(c)).forEach(dateMatch);

  return logs.filter((l) => !used.has(l.key));
}

function callFromLog(log, ctx) {
  const ms = String(log.meetingStatus || '').toLowerCase();
  let status = CALL_STATUS.COMPLETED;
  if (ms === 'canceled' || ms === 'cancelled') status = CALL_STATUS.CANCELED;
  else if (ms === 'rescheduled') status = CALL_STATUS.RESCHEDULED;
  else if (log.when && log.when > ctx.now) status = CALL_STATUS.UPCOMING;
  const host = hostFromInternalUser((ctx.internalUsers || []).find((u) => u.id === log.completedWith));
  const title = log.title || log.deliverableName;
  return baseCall({
    id: `crm:${log.key}`,
    title,
    department: classifyCallDepartment(log.title, log.calendlyEventUri),
    start: log.when,
    timeKnown: log.timeKnown,
    durationMinutes: log.durationMinutes,
    status,
    sources: ['crm'],
    accountId: log.accountId,
    matchedBy: 'crm',
    host,
    calendlyUri: log.calendlyEventUri,
    log,
  });
}

function assignLogStatus(call) {
  if (call.kind !== 'implementation') return LOG_STATUS.NA;
  if (!call.accountId) return LOG_STATUS.UNMATCHED;
  if (call.status === CALL_STATUS.CANCELED || call.status === CALL_STATUS.RESCHEDULED) {
    return call.log ? LOG_STATUS.LOGGED_CANCELED : LOG_STATUS.NA;
  }
  return call.log ? LOG_STATUS.LOGGED : LOG_STATUS.NOT_LOGGED;
}

function assignRecordingStatus(call, now) {
  if (call.recording) return RECORDING_STATUS.RECORDED;
  if (call.status !== CALL_STATUS.COMPLETED || !call.start) return RECORDING_STATUS.NA;
  const endedAt = call.end || new Date(call.start.getTime() + (call.durationMinutes || 60) * 60000);
  if (call.timeKnown && now - endedAt < RECORDING_GRACE_MS) return RECORDING_STATUS.PENDING;
  return RECORDING_STATUS.NOT_RECORDED;
}

// ---------------------------------------------------------------------------
// Assembly
// ---------------------------------------------------------------------------

/**
 * Pure merge of every source into call records. `start`/`end` bound the fetched window;
 * the page narrows further by timeframe.
 */
export function buildCallReport({
  clients,
  membersByAccountId,
  internalUsers,
  calendlyEvents,
  googleResults,
  summaries,
  catalogNames,
  start,
  end,
  now = new Date(),
}) {
  const directory = buildInternalDirectory(internalUsers);
  const matcher = buildAccountMatcher(clients, membersByAccountId || new Map(), directory);
  const internalById = new Map((internalUsers || []).map((u) => [u.id, u]));
  const ctx = { now, directory, matcher, internalUsers, internalById };
  const accountNames = new Map(clients.map((a) => [a.id, a.company || a.name || a.id]));

  const calls = (calendlyEvents || []).map((ev) => callFromCalendly(ev, ctx));

  const googleEntries = dedupeGoogleEvents(googleResults);
  const consumedGoogle = mergeGoogleIntoCalendly(calls, googleEntries);
  googleEntries.forEach((entry) => {
    if (consumedGoogle.has(entry.key)) return;
    const call = callFromGoogle(entry, ctx);
    if (call) calls.push(call);
  });

  const leftoverRecordings = [];
  attachRecordings(calls, summaries || []).forEach((entry) => {
    const call = callFromRecording(entry, ctx);
    if (call) calls.push(call);
    else leftoverRecordings.push(entry);
  });

  const allLogs = collectHoursLogs(clients, catalogNames);
  const windowLogs = allLogs.filter((l) => l.when && l.when >= new Date(start.getTime() - 2 * DAY_MS) && l.when < new Date(end.getTime() + 2 * DAY_MS));
  const unmatchedLogs = reconcileLogs(calls, windowLogs);
  const logCalls = unmatchedLogs
    .filter((l) => l.when >= start && l.when < end)
    .map((log) => callFromLog(log, ctx));
  attachRecordingsByDay(logCalls, leftoverRecordings);
  calls.push(...logCalls);

  calls.forEach((call) => {
    call.accountName = call.accountId ? accountNames.get(call.accountId) || null : null;
    call.logStatus = assignLogStatus(call);
    call.recordingStatus = assignRecordingStatus(call, now);
  });

  const inWindow = calls.filter((c) => c.start && c.start >= start && c.start < end);
  inWindow.sort((a, b) => b.start - a.start);

  return { calls: inWindow, allLogs };
}

async function fetchCalendlyCalls(start, end) {
  try {
    const fn = httpsCallable(getFunctions(), 'fetchCalendlyImplementationCalls', { timeout: 300000 });
    const { data } = await fn({ startDate: start.toISOString(), endDate: end.toISOString() });
    return { ok: true, ...data };
  } catch (err) {
    return { ok: false, code: err?.code || '', message: err?.message || 'Calendly sync failed' };
  }
}

// Program ids as account.program.status names them (mirrors PROGRAM_STATUS_MAP in accountProgramDeliverables).
const PROGRAM_IDS = ['Lite', 'Plus', 'Pro', 'Hourly', '5 Hours', '10 Hours', 'A La Carte', 'Custom'];

/** Deliverable item id → catalog name, from the à la carte catalog + every program's contract items. */
async function fetchCatalogNames(clients) {
  const names = new Map();
  const programIds = new Set(PROGRAM_IDS);
  (clients || []).forEach((a) => {
    const status = typeof a.program === 'string' ? a.program : a.program?.status;
    if (status && status !== 'Not Started') programIds.add(status);
  });
  const [alaCarte, ...programs] = await Promise.all([
    getDocs(collection(db, 'alaCarteItems')).catch(() => null),
    ...[...programIds].map((id) => getDocs(collection(db, 'programs', id, 'contractItems')).catch(() => null)),
  ]);
  [alaCarte, ...programs].forEach((snap) => {
    (snap?.docs || []).forEach((d) => {
      const name = d.data()?.name;
      if (name && !names.has(d.id)) names.set(d.id, name);
    });
  });
  return names;
}

async function fetchRecordings(start, end) {
  try {
    const snap = await getDocs(query(
      collection(db, 'meeting_summaries'),
      where('scheduledStartTime', '>=', start.toISOString()),
      where('scheduledStartTime', '<', end.toISOString())
    ));
    return { ok: true, summaries: snap.docs.map((d) => ({ id: d.id, ...d.data() })) };
  } catch (err) {
    return { ok: false, summaries: [], message: err?.message || 'Could not load Fathom recordings' };
  }
}

/**
 * Load + merge everything for one window. Never throws for a single source failing —
 * each source reports its own state so the page can show what synced.
 */
export async function loadCallReport({ start, end, internalUsers }) {
  const now = new Date();
  const accountsSnap = await getDocs(query(collection(db, 'accounts'), where('type', '==', 'Clients')));
  const clients = accountsSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((a) => a.isDeleted !== true && a.deleted !== true);

  // CRM ▸ My Profile's connected flag is the source of truth for "is Google Calendar connected".
  const googleUsers = (internalUsers || []).filter((u) => u.integrations?.googleCalendar?.connected);
  const fathomUsers = (internalUsers || []).filter((u) => u.integrations?.fathom?.connected);

  const [membersByAccountId, calendly, googleResults, recordings, catalogNames] = await Promise.all([
    fetchPortalUsersLinkedToAccounts(clients).catch(() => new Map()),
    fetchCalendlyCalls(start, end),
    Promise.all(googleUsers.map(async (user) => {
      try {
        return { user, events: await fetchGoogleCalendarEvents(user.id, start, end), ok: true };
      } catch (e) {
        return { user, events: [], ok: false };
      }
    })),
    fetchRecordings(start, end),
    fetchCatalogNames(clients),
  ]);

  const { calls, allLogs } = buildCallReport({
    clients,
    membersByAccountId,
    internalUsers,
    calendlyEvents: calendly.ok ? calendly.events || [] : [],
    googleResults,
    summaries: recordings.summaries,
    catalogNames,
    start,
    end,
    now,
  });

  return {
    calls,
    allLogs,
    clients,
    fetchedAt: now,
    sources: {
      calendly: {
        state: !calendly.ok ? 'error' : calendly.connected === false ? 'not_connected' : 'ok',
        mode: calendly.coverage?.mode || null,
        viaUser: calendly.coverage?.viaUser || null,
        connectedUsers: calendly.coverage?.connectedUsers || [],
        failedUsers: calendly.coverage?.failedUsers || [],
        eventCount: calendly.ok ? (calendly.events || []).length : 0,
        code: calendly.code || null,
        message: calendly.message || null,
      },
      google: {
        connectedUsers: googleUsers.map((u) => internalUserName(u)),
        failedUsers: googleResults.filter((r) => !r.ok).map((r) => internalUserName(r.user)),
        totalUsers: (internalUsers || []).length,
        eventCount: googleResults.reduce((n, r) => n + (r.events || []).length, 0),
      },
      crm: { logCount: allLogs.filter((l) => l.when && l.when >= start && l.when < end).length },
      fathom: {
        state: recordings.ok ? 'ok' : 'error',
        recordingCount: recordings.summaries.length,
        connectedUsers: fathomUsers.map((u) => internalUserName(u)),
        totalUsers: (internalUsers || []).length,
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Aggregations used by the page
// ---------------------------------------------------------------------------

export function summarizeCalls(calls) {
  const count = (status) => calls.filter((c) => c.status === status).length;
  const completed = count(CALL_STATUS.COMPLETED);
  const canceled = count(CALL_STATUS.CANCELED);
  const noShow = count(CALL_STATUS.NO_SHOW);
  const held = calls.filter((c) => c.status === CALL_STATUS.COMPLETED && c.durationMinutes > 0);
  const heldMinutes = held.reduce((sum, c) => sum + c.durationMinutes, 0);
  const attempted = completed + canceled + noShow;
  const recorded = calls.filter((c) => c.recordingStatus === RECORDING_STATUS.RECORDED).length;
  const notRecorded = calls.filter((c) => c.recordingStatus === RECORDING_STATUS.NOT_RECORDED).length;
  return {
    total: calls.length,
    recorded,
    notRecorded,
    recordingRate: recorded + notRecorded ? Math.round((recorded / (recorded + notRecorded)) * 100) : null,
    completed,
    upcoming: count(CALL_STATUS.UPCOMING),
    canceled,
    rescheduled: count(CALL_STATUS.RESCHEDULED),
    noShow,
    heldMinutes,
    avgMinutes: held.length ? Math.round(heldMinutes / held.length) : 0,
    heldRate: attempted ? Math.round((completed / attempted) * 100) : null,
    loggedHours: calls.reduce((sum, c) => sum + (c.log ? c.log.quantity : 0), 0),
    attention: calls.filter(needsAttention).length,
  };
}

export function needsAttention(call) {
  if (call.kind !== 'implementation') return false;
  if (call.logStatus === LOG_STATUS.LOGGED_CANCELED || call.logStatus === LOG_STATUS.UNMATCHED) return true;
  if (call.recordingStatus === RECORDING_STATUS.NOT_RECORDED) return true;
  return call.logStatus === LOG_STATUS.NOT_LOGGED && call.status === CALL_STATUS.COMPLETED;
}

export function groupCallsByAdvisor(calls) {
  const rows = new Map();
  calls.forEach((call) => {
    const id = call.host?.id || (call.host?.name ? `name:${call.host.name}` : 'unassigned');
    if (!rows.has(id)) {
      rows.set(id, {
        id,
        host: call.host,
        total: 0, completed: 0, upcoming: 0, canceled: 0, rescheduled: 0, noShow: 0,
        recorded: 0, notRecorded: 0,
        heldMinutes: 0, heldWithDuration: 0, clients: new Set(),
      });
    }
    const row = rows.get(id);
    row.total += 1;
    if (call.recordingStatus === RECORDING_STATUS.RECORDED) row.recorded += 1;
    if (call.recordingStatus === RECORDING_STATUS.NOT_RECORDED) row.notRecorded += 1;
    if (call.status === CALL_STATUS.COMPLETED) {
      row.completed += 1;
      if (call.durationMinutes > 0) {
        row.heldMinutes += call.durationMinutes;
        row.heldWithDuration += 1;
      }
    }
    if (call.status === CALL_STATUS.UPCOMING) row.upcoming += 1;
    if (call.status === CALL_STATUS.CANCELED) row.canceled += 1;
    if (call.status === CALL_STATUS.RESCHEDULED) row.rescheduled += 1;
    if (call.status === CALL_STATUS.NO_SHOW) row.noShow += 1;
    if (call.accountId) row.clients.add(call.accountId);
  });
  return [...rows.values()]
    .map((row) => {
      const attempted = row.completed + row.canceled + row.noShow;
      return {
        ...row,
        clientCount: row.clients.size,
        avgMinutes: row.heldWithDuration ? Math.round(row.heldMinutes / row.heldWithDuration) : 0,
        heldRate: attempted ? Math.round((row.completed / attempted) * 100) : null,
        recordingRate: row.recorded + row.notRecorded
          ? Math.round((row.recorded / (row.recorded + row.notRecorded)) * 100)
          : null,
      };
    })
    .sort((a, b) => {
      if ((a.id === 'unassigned') !== (b.id === 'unassigned')) return a.id === 'unassigned' ? 1 : -1;
      return b.total - a.total;
    });
}

/**
 * One row per client: calls in the window plus "last call" from the full hours-log history
 * (so a quiet month still shows when they were last on a call). Active clients with no calls
 * are included when `includeIdleClients` is set.
 */
export function groupCallsByClient(calls, { clients, allLogs, includeIdleClients, now = new Date() }) {
  const rows = new Map();
  const ensure = (accountId, name) => {
    if (!rows.has(accountId)) {
      rows.set(accountId, {
        accountId,
        accountName: name,
        completed: 0, upcoming: 0, canceled: 0, rescheduled: 0, noShow: 0,
        heldMinutes: 0, loggedHours: 0, attention: 0,
        lastCall: null, nextCall: null,
      });
    }
    return rows.get(accountId);
  };

  calls.forEach((call) => {
    if (!call.accountId) return;
    const row = ensure(call.accountId, call.accountName);
    if (call.status === CALL_STATUS.COMPLETED) {
      row.completed += 1;
      row.heldMinutes += call.durationMinutes || 0;
      if (!row.lastCall || call.start > row.lastCall) row.lastCall = call.start;
    }
    if (call.status === CALL_STATUS.UPCOMING) {
      row.upcoming += 1;
      if (!row.nextCall || call.start < row.nextCall) row.nextCall = call.start;
    }
    if (call.status === CALL_STATUS.CANCELED) row.canceled += 1;
    if (call.status === CALL_STATUS.RESCHEDULED) row.rescheduled += 1;
    if (call.status === CALL_STATUS.NO_SHOW) row.noShow += 1;
    if (call.log) row.loggedHours += call.log.quantity;
    if (needsAttention(call)) row.attention += 1;
  });

  if (includeIdleClients) {
    (clients || []).filter(isActiveClientAccount).forEach((a) => ensure(a.id, a.company || a.name || a.id));
  }

  // Last call from the hours log history, for clients whose last call predates the window.
  (allLogs || []).forEach((log) => {
    const row = rows.get(log.accountId);
    if (!row || !log.when || log.when > now) return;
    const ms = String(log.meetingStatus || '').toLowerCase();
    if (ms === 'canceled' || ms === 'cancelled' || ms === 'rescheduled') return;
    if (!row.lastCall || log.when > row.lastCall) row.lastCall = log.when;
  });

  return [...rows.values()].map((row) => ({
    ...row,
    daysSinceLastCall: row.lastCall ? Math.floor((now - row.lastCall) / DAY_MS) : null,
  }));
}
